import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Navigation, Footer, WhatsAppButton, CTASection, PageHeader } from "@/components/Layout";
import { Target, Rocket, Lightbulb, Users, Sparkles, Shield, Heart, Award } from "lucide-react";

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="about-page" className="min-h-screen">
      <Navigation isScrolled={true} forceDark={true} />
      
      <PageHeader 
        badge="About Infinium"
        title="Who We Are"
        subtitle="A strategy and innovation consultancy working where complexity meets execution."
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Our Purpose</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Helping organisations make <span className="text-gradient">better decisions</span></h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">To help organisations make better decisions in complex environments and translate strategy into action.</p>
              <p className="text-slate-500 leading-relaxed">We work alongside leadership teams to balance strategic clarity with practical delivery. We design solutions that can be implemented in real-world conditions, not just theoretical frameworks.</p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1758518730083-4c12527b6742?auto=format&fit=crop&w=800&q=80" alt="Strategy session" className="w-full h-auto object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Our Beliefs</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">What We <span className="text-gradient">Stand For</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-8">
                <div className="service-icon-container mb-6"><Target className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Complexity requires structure</h3>
                <p className="text-slate-500">We bring clarity to ambiguous situations through rigorous analysis and structured thinking.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-8">
                <div className="service-icon-container mb-6"><Rocket className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Strategy must lead to execution</h3>
                <p className="text-slate-500">Ideas without implementation create no value. We focus on actionable outcomes.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-8">
                <div className="service-icon-container mb-6"><Lightbulb className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Innovation must solve real problems</h3>
                <p className="text-slate-500">We ground innovation in practical application and measurable impact.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-white border-slate-100 rounded-2xl">
              <CardContent className="p-8">
                <div className="service-icon-container mb-6"><Users className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Collaboration enables outcomes</h3>
                <p className="text-slate-500">Sustainable results come from working alongside clients, not for them.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-cyan-50 text-cyan-600 mb-6">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Principles That <span className="text-gradient">Guide Us</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="card-hover bg-slate-50 border-slate-100 rounded-2xl text-center">
              <CardContent className="p-8">
                <div className="service-icon-container mx-auto mb-6"><Sparkles className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Clarity over complexity</h3>
                <p className="text-slate-500 text-sm">We simplify the complex without losing what matters.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-slate-50 border-slate-100 rounded-2xl text-center">
              <CardContent className="p-8">
                <div className="service-icon-container mx-auto mb-6"><Shield className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Integrity and independence</h3>
                <p className="text-slate-500 text-sm">We provide honest counsel, even when it's difficult.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-slate-50 border-slate-100 rounded-2xl text-center">
              <CardContent className="p-8">
                <div className="service-icon-container mx-auto mb-6"><Heart className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Collaboration and trust</h3>
                <p className="text-slate-500 text-sm">We build lasting partnerships based on mutual respect.</p>
              </CardContent>
            </Card>
            <Card className="card-hover bg-slate-50 border-slate-100 rounded-2xl text-center">
              <CardContent className="p-8">
                <div className="service-icon-container mx-auto mb-6"><Award className="text-cyan-500" size={28} strokeWidth={1.5} /></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Long-term impact</h3>
                <p className="text-slate-500 text-sm">We measure success by sustainable outcomes, not quick wins.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
